/* ===== Documents Library Page ===== */

import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Document, DocumentCategory } from '@zetyar/domain'
import { Download, FilePlus2, FileText, Folder, Image as ImageIcon, Trash2, Upload } from 'lucide-react'
import { useApp } from '@/presentation/providers/useApp'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Button } from '@/presentation/components/ui/Button'
import { Badge } from '@/presentation/components/ui/Badge'
import { createWebTenantContext } from '@/infrastructure/repositories/announcements'
import { webDocumentRepository } from '@/infrastructure/repositories/documents'
import { formatFileSize } from '@/infrastructure/storage/documents'
import { useReauthGuard } from '@/presentation/hooks/useReauthGuard'
import { DocumentUploadModal } from './DocumentUploadModal'
import { DocumentComposerModal } from './DocumentComposerModal'

const PAGE_SIZE = 50

export function DocumentsPage() {
  const { currentMembership, currentOrganization, profile, user, permissions } = useApp()
  const { guard, reauthModal } = useReauthGuard()
  const canUpload = Boolean(profile?.isSuperAdmin) || permissions.includes('documents.upload')
  const canDelete = Boolean(profile?.isSuperAdmin) || permissions.includes('documents.delete')

  const [documents, setDocuments] = useState<Document[]>([])
  const [categories, setCategories] = useState<DocumentCategory[]>([])
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isUploadOpen, setIsUploadOpen] = useState(false)
  const [isComposerOpen, setIsComposerOpen] = useState(false)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)

  const organizationId = currentOrganization?.id
  const membershipId = currentMembership?.id
  const context = useMemo(() => {
    if (!organizationId || !profile || !user) return null
    return createWebTenantContext({ authUid: user.uid, personId: profile.id, organizationId, membershipId })
  }, [organizationId, membershipId, profile, user])

  const load = useCallback(async () => {
    if (!context) {
      setDocuments([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)
    try {
      const [items, tags] = await Promise.all([
        webDocumentRepository.listVisible(context, {
          limit: PAGE_SIZE,
          categoryId: categoryFilter === 'all' ? undefined : categoryFilter,
        }),
        webDocumentRepository.listCategories ? webDocumentRepository.listCategories(context) : Promise.resolve([]),
      ])
      setDocuments(items)
      setCategories(tags)
    } catch (caught) {
      console.error('Error al cargar documentos:', caught)
      setDocuments([])
      setError('No se pudieron cargar los documentos.')
    } finally {
      setIsLoading(false)
    }
  }, [context, categoryFilter])

  useEffect(() => {
    void load()
  }, [load])

  const download = async (doc: Document) => {
    const getDownloadUrl = webDocumentRepository.getDownloadUrl
    if (!context || !getDownloadUrl) return
    setDownloadingId(doc.id)
    setError(null)
    try {
      const url = await getDownloadUrl(context, doc.storagePath)
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (caught) {
      console.error('Error al generar el enlace de descarga:', caught)
      setError('No se pudo generar el enlace de descarga.')
    } finally {
      setDownloadingId(null)
    }
  }

  const remove = (doc: Document) => {
    guard(async () => {
      if (!context || !webDocumentRepository.delete) return
      if (!confirm(`¿Eliminar "${doc.name}"? Los miembros dejarán de verlo.`)) return
      try {
        await webDocumentRepository.delete(context, doc.id)
        setDocuments((current) => current.filter((item) => item.id !== doc.id))
      } catch (caught) {
        console.error('Error al eliminar el documento:', caught)
        setError('No se pudo eliminar el documento.')
      }
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-neutral-900 dark:text-neutral-100">Documentos</h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            Archivos, convenios, reglamentos y actas de {currentOrganization?.name}.
          </p>
        </div>
        {canUpload && context && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" leftIcon={<Upload className="h-4 w-4" />} onClick={() => setIsUploadOpen(true)}>
              Subir archivo
            </Button>
            <Button leftIcon={<FilePlus2 className="h-4 w-4" />} onClick={() => setIsComposerOpen(true)}>
              Crear documento
            </Button>
          </div>
        )}
      </div>

      {error && (
        <div role="alert" className="rounded-[13px] border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700 dark:border-danger-500/30 dark:bg-danger-500/10 dark:text-danger-300">
          {error}
        </div>
      )}

      {categories.length > 0 && (
        <div role="group" aria-label="Filtrar por etiqueta" className="flex flex-wrap gap-1.5">
          {[{ id: 'all', name: 'Todas' }, ...categories].map((tag) => (
            <button
              key={tag.id}
              type="button"
              aria-pressed={categoryFilter === tag.id}
              onClick={() => setCategoryFilter(tag.id)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${
                categoryFilter === tag.id
                  ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-500/15 dark:text-primary-200'
                  : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800'
              }`}
            >
              {tag.name}
            </button>
          ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Folder className="h-5 w-5 text-primary-600" />
            <CardTitle>Archivos disponibles</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="py-6 text-sm text-neutral-500 dark:text-neutral-400">Cargando documentos...</p>
          ) : documents.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                {categoryFilter === 'all'
                  ? `No hay documentos cargados en ${currentOrganization?.name}.`
                  : 'No hay documentos con esta etiqueta.'}
              </p>
              {canUpload && context && categoryFilter === 'all' && (
                <Button className="mt-4" variant="outline" leftIcon={<Upload className="h-4 w-4" />} onClick={() => setIsUploadOpen(true)}>
                  Subir el primero
                </Button>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {documents.map((doc) => {
                const Icon = doc.fileType.startsWith('image/') ? ImageIcon : FileText
                return (
                  <li key={doc.id} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[12px] bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-300">
                        <Icon className="h-5 w-5" aria-hidden="true" />
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-neutral-900 dark:text-neutral-100">{doc.name}</p>
                        <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">
                          {[
                            doc.fileName,
                            formatFileSize(doc.fileSize),
                            new Date(doc.createdAt).toLocaleDateString('es-CL'),
                            doc.description,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                      {doc.category && <Badge variant="neutral">{doc.category.name}</Badge>}
                      <Button
                        size="sm"
                        variant="outline"
                        leftIcon={<Download className="h-3.5 w-3.5" />}
                        isLoading={downloadingId === doc.id}
                        onClick={() => void download(doc)}
                      >
                        Descargar
                      </Button>
                      {canDelete && (
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`Eliminar ${doc.name}`}
                          leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                          onClick={() => remove(doc)}
                        >
                          Eliminar
                        </Button>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {isUploadOpen && context && (
        <DocumentUploadModal
          context={context}
          categories={categories}
          onClose={() => setIsUploadOpen(false)}
          onUploaded={() => {
            setIsUploadOpen(false)
            void load()
          }}
        />
      )}

      {isComposerOpen && context && (
        <DocumentComposerModal
          context={context}
          organizationName={currentOrganization?.name ?? 'La organización'}
          logoUrl={currentOrganization?.logoUrl ?? null}
          categories={categories}
          defaultSignerName={currentMembership?.displayName || profile?.displayName || ''}
          defaultSignerPosition={currentMembership?.position ?? ''}
          onClose={() => setIsComposerOpen(false)}
          onCreated={() => {
            setIsComposerOpen(false)
            void load()
          }}
        />
      )}

      {reauthModal}
    </div>
  )
}
